import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { CalendarRange, ListChecks, TrendingUp, ChevronRight } from "lucide-react";

/**
 * A20-P9.1 — Dashboard tile: wishlists with planned (calendar-linked) events.
 *
 * Strict per-user (backend filters by owner_id). EUR-only context.
 * Reuses the `_wishlist_rollups` helper server-side; renders nothing
 * intrusive in the zero state (still shown so the feature is discoverable).
 *
 * No props — fetches `/api/wishlists/analytics/with-planned-events`.
 */
const ZERO = {
  wishlists_with_planned_events_count: 0,
  linked_items_count: 0,
  average_progress_percentage: 0.0,
  next_deadline: null,
  overdue_items_count: 0,
  top_wishlists: [],
};

export const WishlistsPlannedTile = () => {
  const { t } = useLang();
  const [data, setData] = useState(ZERO);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/wishlists/analytics/with-planned-events")
      .then((r) => { if (alive) { setData(r.data || ZERO); setLoaded(true); } })
      .catch(() => { if (alive) { setData(ZERO); setLoaded(true); } });
    return () => { alive = false; };
  }, []);

  if (!loaded) return null;

  const count = data.wishlists_with_planned_events_count || 0;
  const hasData = count > 0;
  const linked = data.linked_items_count || 0;
  const avg = Number(data.average_progress_percentage || 0);
  const next = data.next_deadline || null;
  const overdue = data.overdue_items_count || 0;
  const top = Array.isArray(data.top_wishlists) ? data.top_wishlists : [];

  const fmtPct = (n) => `${Math.round(n * 10) / 10}%`;
  const dash = t("wishlist.plannedTile.kpiNoDeadline");

  return (
    <div className="tsw-card p-5 space-y-4" data-testid="dashboard-wishlists-planned-tile">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-semibold text-base text-slate-900 inline-flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-violet-700" />
          {t("wishlist.plannedTile.title")}
        </h3>
        {hasData && overdue > 0 && (
          <span
            className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200"
            data-testid="dashboard-wishlists-planned-overdue-badge"
          >
            {t("wishlist.plannedTile.overdueBadge").replace("{n}", overdue)}
          </span>
        )}
      </div>

      {!hasData ? (
        <div className="space-y-3" data-testid="dashboard-wishlists-planned-empty">
          <p className="text-sm font-medium text-slate-700">
            {t("wishlist.plannedTile.emptyHeadline")}
          </p>
          <p className="text-xs text-slate-500 leading-relaxed">
            {t("wishlist.plannedTile.emptyBody")}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Link
              to="/app/wishlists"
              className="inline-flex items-center gap-1 text-xs font-medium text-violet-800 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-full px-3 py-1.5 transition"
              data-testid="dashboard-wishlists-planned-cta-wishlists"
            >
              <ListChecks className="w-3.5 h-3.5" />
              {t("wishlist.plannedTile.ctaViewWishlists")}
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500 -mt-2">
            {t("wishlist.plannedTile.subtitle")}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
              <div className="text-[11px] uppercase tracking-wide text-violet-700">
                {t("wishlist.plannedTile.kpiWishlists")}
              </div>
              <div
                className="text-2xl font-display font-semibold text-slate-900 tabular-nums mt-1"
                data-testid="dashboard-wishlists-planned-count"
              >
                {count}
              </div>
            </div>
            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
              <div className="text-[11px] uppercase tracking-wide text-violet-700">
                {t("wishlist.plannedTile.kpiLinkedItems")}
              </div>
              <div
                className="text-2xl font-display font-semibold text-slate-900 tabular-nums mt-1"
                data-testid="dashboard-wishlists-planned-linked-items"
              >
                {linked}
              </div>
            </div>
            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
              <div className="text-[11px] uppercase tracking-wide text-violet-700 inline-flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                {t("wishlist.plannedTile.kpiAvgProgress")}
              </div>
              <div
                className="text-2xl font-display font-semibold text-slate-900 tabular-nums mt-1"
                data-testid="dashboard-wishlists-planned-avg-progress"
              >
                {fmtPct(avg)}
              </div>
            </div>
            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
              <div className="text-[11px] uppercase tracking-wide text-violet-700">
                {t("wishlist.plannedTile.kpiNextDeadline")}
              </div>
              <div
                className="text-base font-semibold text-slate-900 tabular-nums mt-1 truncate"
                data-testid="dashboard-wishlists-planned-next-deadline"
              >
                {next || dash}
              </div>
            </div>
          </div>

          {top.length > 0 && (
            <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
              {top.map((w) => (
                <li
                  key={w.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50 transition"
                  data-testid={`dashboard-wishlists-planned-row-${w.id}`}
                >
                  <Link
                    to="/app/wishlists"
                    className="flex-1 min-w-0 text-sm text-slate-800 hover:text-violet-800 truncate"
                  >
                    {w.title || "—"}
                  </Link>
                  <div className="flex items-center gap-2 text-[11px] text-slate-600 shrink-0">
                    <span className="bg-slate-100 rounded px-1.5 py-0.5">
                      {t("wishlist.plannedTile.rowItemsShort").replace("{n}", w.linked_items_count)}
                    </span>
                    <span className="bg-violet-50 text-violet-800 border border-violet-100 rounded px-1.5 py-0.5 tabular-nums">
                      {fmtPct(w.progress_percentage || 0)}
                    </span>
                    {w.next_deadline && (
                      <span className="hidden sm:inline tabular-nums text-slate-500">
                        {w.next_deadline}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Link
              to="/app/wishlists"
              className="inline-flex items-center gap-1 text-xs font-medium text-violet-800 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-full px-3 py-1.5 transition"
              data-testid="dashboard-wishlists-planned-cta-wishlists"
            >
              <ListChecks className="w-3.5 h-3.5" />
              {t("wishlist.plannedTile.ctaViewWishlists")}
            </Link>
            <Link
              to="/app/calendar"
              className="inline-flex items-center gap-1 text-xs font-medium text-sky-800 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-full px-3 py-1.5 transition"
              data-testid="dashboard-wishlists-planned-cta-calendar"
            >
              <CalendarRange className="w-3.5 h-3.5" />
              {t("wishlist.plannedTile.ctaViewCalendar")}
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default WishlistsPlannedTile;
